import React, { useEffect, useMemo, useRef } from 'react';

import { AppFormBirthDateInput, AppFormInput, AppFormSearchableSelect } from '../../../components/common/appForms';
import CountryFlag from '../../../components/common/CountryFlag/CountryFlag';
import type { SelectOption } from '../../../components/common/SearchableSelect/SearchableSelect';

import { CALLING_CODES, CODE_TO_ISO, extractCallingCode } from '../../../constants/callingCodes';

import { maskPhoneByCountry, phoneMaxLength, phonePlaceholder, unmaskPhone } from '../../../utils/masks';

import type { CheckoutPersonalData } from '../checkout.types';

import { maskCheckoutPersonalField } from '../checkout.utils';

import pageStyles from '../CheckoutPage.module.css';

interface PersonalDataStepProps {
  data: CheckoutPersonalData;
  errors: Record<string, string>;
  requiresCpf?: boolean;
  showBirthDate?: boolean;
  showCpf?: boolean;
  emailAfterPhone?: boolean;
  phoneLabel?: string;
  disabled?: boolean;
  readOnlyFields?: Partial<Pick<CheckoutPersonalData, 'fullName' | 'email' | 'callingCode' | 'phone'>>;
  onFieldChange: <K extends keyof CheckoutPersonalData>(
    field: K,
    value: CheckoutPersonalData[K],
  ) => void;
  onFieldBlur?: (field: 'email' | 'cpf' | 'phone') => void;
}

const sortWithBrazilFirst = <T extends { value: string; label: string }>(
  options: T[],
  brazilValue = '55',
): T[] => {
  const sorted = [...options].sort((left, right) => left.label.localeCompare(right.label, 'pt'));
  const brazilIndex = sorted.findIndex((option) => option.value === brazilValue);

  if (brazilIndex > 0) {
    const [brazilOption] = sorted.splice(brazilIndex, 1);
    sorted.unshift(brazilOption);
  }

  return sorted;
};

const formatCallingCodeTrigger = (option: SelectOption) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CountryFlag code={CODE_TO_ISO[option.value] || ''} size={16} />
    +{extractCallingCode(option.value)}
  </span>
);

const formatCallingCodeOption = (option: SelectOption) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CountryFlag code={CODE_TO_ISO[option.value] || ''} size={16} />
    {option.label}
  </span>
);

const PersonalDataStep: React.FC<PersonalDataStepProps> = ({
  data,
  errors,
  requiresCpf = true,
  showBirthDate = true,
  showCpf = true,
  emailAfterPhone = false,
  phoneLabel = 'Celular',
  disabled = false,
  readOnlyFields,
  onFieldChange,
  onFieldBlur,
}) => {
  const prevCallingCode = useRef(data.callingCode);

  const callingCodeOptions = useMemo(
    () => sortWithBrazilFirst(CALLING_CODES).map((entry) => ({
      value: entry.value,
      label: entry.label,
    })),
    [],
  );

  const isFullNameReadOnly = Boolean(readOnlyFields?.fullName);
  const isEmailReadOnly = Boolean(readOnlyFields?.email);
  const isCallingCodeReadOnly = Boolean(readOnlyFields?.callingCode);
  const isPhoneReadOnly = Boolean(readOnlyFields?.phone);

  useEffect(() => {
    if (prevCallingCode.current === data.callingCode) {
      return;
    }

    prevCallingCode.current = data.callingCode;

    if (!data.phone) {
      return;
    }

    const rawPhone = unmaskPhone(data.phone);
    onFieldChange(
      'phone',
      maskPhoneByCountry(rawPhone, data.callingCode || '55', { preferMobile: true }),
    );
  }, [data.callingCode, data.phone, onFieldChange]);

  const handleChange = (field: keyof CheckoutPersonalData, rawValue: string) => {
    const maskedValue = maskCheckoutPersonalField(
      field,
      rawValue,
      data.callingCode,
      'BR',
    ) as CheckoutPersonalData[typeof field];

    onFieldChange(field, maskedValue);
  };

  const emailInput = (
    <AppFormInput
      id="checkout-email"
      name="email"
      label="E-mail"
      type="email"
      required
      value={data.email}
      onChange={(event) => onFieldChange('email', event.target.value)}
      onBlur={() => onFieldBlur?.('email')}
      placeholder="email@exemplo.com"
      error={errors.email}
      disabled={disabled}
      readOnly={isEmailReadOnly}
      containerClassName={pageStyles.fieldRowItem}
    />
  );

  const shouldRenderContactRow = showBirthDate || !emailAfterPhone || showCpf;

  return (
    <div className={pageStyles.formFields}>
      <AppFormInput
        id="checkout-full-name"
        name="fullName"
        label="Nome completo"
        required
        value={data.fullName}
        onChange={(event) => handleChange('fullName', event.target.value)}
        onBlur={() => {
          if (data.fullName.trim()) {
            onFieldChange('fullName', data.fullName);
          }
        }}
        placeholder="Digite seu nome completo"
        error={errors.fullName}
        disabled={disabled}
        readOnly={isFullNameReadOnly}
      />

      {shouldRenderContactRow && (
        <div className={pageStyles.fieldRowContact}>
          {showBirthDate && (
            <AppFormBirthDateInput
              id="checkout-birth-date"
              name="birthDate"
              label="Data de nascimento"
              required
              value={data.birthDate}
              onChange={(isoDate) => onFieldChange('birthDate', isoDate)}
              placeholder="DD/MM/AAAA"
              error={errors.birthDate}
              disabled={disabled}
              containerClassName={pageStyles.fieldRowItem}
              plainCalendarButton
            />
          )}

          {!emailAfterPhone && emailInput}

          {showCpf && (
            <AppFormInput
              id="checkout-cpf"
              name="cpf"
              label="CPF"
              value={data.cpf}
              onChange={(event) => handleChange('cpf', event.target.value)}
              onBlur={() => onFieldBlur?.('cpf')}
              placeholder="000.000.000-00"
              inputMode="numeric"
              maxLength={14}
              required={requiresCpf}
              error={errors.cpf}
              disabled={disabled}
              containerClassName={pageStyles.fieldRowItem}
            />
          )}
        </div>
      )}

      <div className={pageStyles.fieldRowPhone}>
        <AppFormSearchableSelect
          id="checkout-calling-code"
          label="Código do país"
          required
          options={callingCodeOptions}
          value={data.callingCode || '55'}
          onChange={(event) => onFieldChange('callingCode', event.target.value)}
          placeholder="Brasil (+55)"
          searchPlaceholder="Pesquisar"
          noResultsText="Nenhum código encontrado"
          disabled={disabled}
            readOnly={isCallingCodeReadOnly}
          containerClassName={pageStyles.fieldRowItem}
          allowEmpty={false}
          triggerFormat={formatCallingCodeTrigger}
          optionFormat={formatCallingCodeOption}
        />

        <AppFormInput
          id="checkout-phone"
          name="phone"
          label={phoneLabel}
          required
          value={data.phone}
          onChange={(event) => handleChange('phone', event.target.value)}
          onBlur={() => onFieldBlur?.('phone')}
          placeholder={phonePlaceholder(data.callingCode || '55')}
          inputMode="tel"
          maxLength={phoneMaxLength(data.callingCode || '55')}
          error={errors.phone}
          disabled={disabled}
          readOnly={isPhoneReadOnly}
          containerClassName={pageStyles.fieldRowItem}
        />
      </div>

      {emailAfterPhone && emailInput}
    </div>
  );
};

export default PersonalDataStep;
